// The rule figures the point of interest marks draw to scale (R2714,
// mockup round 114, v2-the-deep.md §4.2, §5.1, §8.3).
//
// The hub sends the figures of the applied pack on `Hello.catalog.rules`
// (`poi_gate_mul`, `poi_gate_reach_cells`, `poi_lair_home_px`, and
// `poi_vent_stock` of R2735) while the world latched `discovery`, so a balance
// patch of a running server moves the marks. `GameState.setHello` hands
// each hello catalog to `setPoiRules`. The hub page and the workshop and
// MODDING previews open no world socket and get no such hello: there, and
// for each figure a hello does not carry (0), the compile-time copy of
// `gen/balance.ts` stays as the fallback; the vent stock has none, because
// only a world hello brings the live rows it divides. This module is the
// one reader.

import type { Catalog } from '../gen/proto/aquaphobia';
import { POI_GATE_MUL, POI_GATE_REACH_CELLS, POI_LAIR_HOME_PX } from '../gen/balance';

/** The figures a mark reads. */
export interface PoiRules {
  /** A current gate multiplies the current by this (world.toml [world.poi]). */
  gateMul: number;
  /** The hex steps a current gate reaches. */
  gateReachCells: number;
  /** The home radius of a monster round its lair, in world px (npc.toml). */
  lairHomePx: number;
  /** The stock of a full vent in isotopes (`[sim.vents]`, R2735), or 0
   *  before a hello carries it: then a mark draws no share. */
  ventStock: number;
}

/** The figures of the pack the client was built with. */
const BUILT: PoiRules = {
  gateMul: POI_GATE_MUL,
  gateReachCells: POI_GATE_REACH_CELLS,
  lairHomePx: POI_LAIR_HOME_PX,
  // No compile-time copy: only a world hello brings live rows to share.
  ventStock: 0,
};

let rules: PoiRules = BUILT;

/** Takes the figures of a hello catalog (R2714): a non-zero figure of
 *  `catalog.rules` wins, and the built figure stands for one the hello
 *  does not carry. A catalog with none, or no catalog, restores the built
 *  figures. `GameState.setHello` calls it. */
export function setPoiRules(catalog: Catalog | null | undefined): void {
  const r = catalog?.rules;
  rules = {
    gateMul: r?.poiGateMul || BUILT.gateMul,
    gateReachCells: r?.poiGateReachCells || BUILT.gateReachCells,
    lairHomePx: r?.poiLairHomePx || BUILT.lairHomePx,
    ventStock: r?.poiVentStock || BUILT.ventStock,
  };
}

/** The figures of the last hello, or of the built pack before one. */
export function poiRules(): PoiRules {
  return rules;
}
